import { Employee } from "domain/entities/employee";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useFilterEmployeeStore } from "presentation/stores/use-filter-employee-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContractTypeDropdown from "./ContractTypeDropdown";
import HeaderEmployeeGrid from "./HeaderEmployeeGrid";
import JobTitleDropdown from "./JobTitleDropdown";
import EmployeeStatusDropdown from "./StatusEmployeeDropdown";
import StatusEmployeeSwitch from "./StatusEmployeeSwitch";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";
import employeeRepository from "infra/repositories/employee-repository";
import { useDocumentUploadStore } from "presentation/stores/use-document-upload-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFunctions } from "presentation/hooks/use-query-functions";
import { BaseInputFilter } from "domain/models/base-input-filter";
import {
  exportToExcel,
  exportToPdf,
  exportMultiLineToPdf,
  getFullText,
  ExportConfig,
  MultiLineExportConfig,
} from "presentation/utils/exportUtils";

const EmployeeGrid: React.FC = () => {
  const navigate = useNavigate();
  const { userPermissions } = useAuthStore();
  const { filters, setFilters } = useFilterEmployeeStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "employeeNumber",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });
  const { documentsUpload, setDocumentsUpload } = useDocumentUploadStore();

  const {
    data: employees,
    isLoading,
    isError,
    error,
    refetch,
  } = useEmployeesByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as EmployeeInputFilter);

  const { data: functionsData } = useFunctions({
    limit: 100,
    sortBy: "label",
    sortOrder: "asc",
  } as BaseInputFilter);

  // Função para verificar se o usuário é admin
  const isUserAdmin = (): boolean => {
    const permFunc = functionsData?.data?.find(
      (f) => f.label === "Colaboradores"
    );
    const permissionsEmployeeGrid = userPermissions?.find(
      (p) => p.functionId === permFunc?.id
    );
    return permissionsEmployeeGrid?.allowEdit ?? false;
  };

  const getAllEmployeesForExport = async (): Promise<Employee[]> => {
    try {
      const result = await employeeRepository.getAll({
        sortBy: lazyParams.sortBy,
        sortOrder: lazyParams.sortOrder,
        page: 1,
        limit: 100,
      } as EmployeeInputFilter);

      return result.data || [];
    } catch (error) {
      console.error(" Erro ao buscar colaboradores:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao buscar dados para exportação.",
        life: 3000,
      });
      return employees?.data || [];
    }
  };

  const handleExportExcel = async () => {
    const allEmployees = await getAllEmployeesForExport();

    if (allEmployees.length === 0) {
      return;
    }

    try {
      const isAdmin = isUserAdmin();

      const columns = isAdmin
        ? [
            { header: "Nº Colaborador", dataKey: "employeeNumber" },
            { header: "Nome Completo", dataKey: "name" },
            { header: "Cargo", dataKey: "jobTitle.name" },
            { header: "Ativo", dataKey: "isActive" },
            { header: "Email Corporativo", dataKey: "emailAddress" },
            { header: "Email Pessoal", dataKey: "personalEmail" },
            { header: "Telefone", dataKey: "phoneNumber" },
            {
              header: "Telemóvel Profissional",
              dataKey: "professionalPhoneNumber",
            },
            { header: "Data Nascimento", dataKey: "birthDate" },
            { header: "Estado Civil", dataKey: "maritalStatus" },
            { header: "Nº Filhos", dataKey: "childrenNumber" },
            { header: "Tipo Doc. Identificação", dataKey: "idDocumentType" },
            { header: "Nº Doc. Identificação", dataKey: "idDocumentNumber" },
            { header: "NIF", dataKey: "fiscalIdNumber" },
            { header: "Nº Segurança Social", dataKey: "socialSecurityNumber" },
            { header: "Nº Carta Condução", dataKey: "drivingLicenceNumber" },
            { header: "RFI Expira em", dataKey: "rfiExpiresAt" },
            { header: "Morada", dataKey: "address" },
            { header: "Complemento", dataKey: "complement" },
            { header: "Código Postal", dataKey: "postalCode" },
            { header: "Cidade", dataKey: "city" },
            { header: "País", dataKey: "country" },
            { header: "Local de Trabalho", dataKey: "workLocation" },
            { header: "País de Trabalho", dataKey: "workCountry" },
            { header: "Tipo de Trabalho", dataKey: "workType" },
            { header: "Tipo Contrato", dataKey: "contractType" },
            { header: "Data Admissão", dataKey: "admissionDate" },
            { header: "Data Rescisão", dataKey: "terminationDate" },
            { header: "Gestor Associado", dataKey: "managerAssociated.name" },
            { header: "Conta Bancária", dataKey: "bankAccount" },
            { header: "Valor KM/Mês", dataKey: "kmValueMonth" },
            { header: "Proprietário Carro", dataKey: "carOwner" },
            { header: "Matrícula Carro", dataKey: "carRegistration" },
            {
              header: "Dias Férias Ano Atual",
              dataKey: "vacationDaysCurrentYear",
            },
            {
              header: "Dias Férias Ano Anterior",
              dataKey: "vacationDaysLastYear",
            },
            {
              header: "Nome Contacto Emergência",
              dataKey: "emergencyContactName",
            },
            {
              header: "Relação Contacto Emergência",
              dataKey: "emergencyContactRelation",
            },
            {
              header: "Telefone Contacto Emergência",
              dataKey: "emergencyContactPhone",
            },
            { header: "Serviço de Suporte", dataKey: "isSuportService" },
            { header: "Observações", dataKey: "observations" },
            { header: "Criado em", dataKey: "createdAt" },
            { header: "Modificado em", dataKey: "modifiedAt" },
            { header: "Modificado por", dataKey: "lastModifiedBy" },
          ]
        : [
            { header: "Nº", dataKey: "employeeNumber" },
            { header: "Nome", dataKey: "name" },
            { header: "Cargo", dataKey: "jobTitle.name" },
            { header: "Email", dataKey: "emailAddress" },
            { header: "Telefone", dataKey: "phoneNumber" },
            { header: "Local", dataKey: "workLocation" },
          ];

      const config: ExportConfig = {
        filename: "colaboradores",
        title: isAdmin
          ? "Relatório Completo de Colaboradores"
          : "Lista de Colaboradores",
        subtitle: isAdmin
          ? "Dados completos com todos os campos disponíveis"
          : "Informações básicas dos colaboradores",
        columns,
      };

      const exportData = allEmployees.map((emp) => {
        const flatData: any = {};
        columns.forEach((col) => {
          const keys = col.dataKey.split(".");
          let value = emp as any;
          for (const key of keys) {
            value = value?.[key];
          }
          if (col.dataKey.includes("Date") && value) {
            flatData[col.dataKey] = new Date(value).toLocaleDateString("pt-PT");
          } else if (col.dataKey === "isActive") {
            flatData[col.dataKey] = value ? "Ativo" : "Inativo";
          } else {
            flatData[col.dataKey] = value;
          }
        });
        return flatData;
      });

      await exportToExcel(exportData, config);

      toast.current?.show({
        severity: "success",
        summary: "Excel Gerado",
        detail: `✅ ${allEmployees.length} colaboradores exportados com sucesso!`,
        life: 4000,
      });
    } catch (error) {
      console.error("Erro ao gerar Excel:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao gerar arquivo Excel. Tente novamente.",
        life: 3000,
      });
    }
  };

  const handleExportPdf = async () => {
    const allEmployees = await getAllEmployeesForExport();

    if (allEmployees.length === 0) {
      return;
    }

    try {
      const isAdmin = isUserAdmin();

      // Configurar colunas baseadas no nível de acesso
      const columns = isAdmin
        ? [
            // Layout tipo "cartão" bem organizado e fácil de identificar
            { header: "COLABORADOR", dataKey: "funcionario", width: 200 },
            {
              header: "CONTACTOS & DOCUMENTOS",
              dataKey: "contactosDoc",
              width: 200,
            },
            {
              header: "TRABALHO & OUTROS",
              dataKey: "trabalhoOutros",
              width: 200,
            },
          ]
        : [
            { header: "Nº", dataKey: "employeeNumber" },
            { header: "Nome", dataKey: "name" },
            { header: "Cargo", dataKey: "jobTitle" },
            { header: "Email", dataKey: "email" },
            { header: "Telefone", dataKey: "phone" },
            { header: "Local", dataKey: "location" },
          ];

      // Configuração de exportação
      const config: ExportConfig | MultiLineExportConfig = isAdmin
        ? {
            filename: "colaboradores_cartoes_completos",
            title: "CARTÕES DOS COLABORADORES - DADOS COMPLETOS",
            subtitle: "",
            orientation: "landscape",
            showPageNumbers: true,
            columns: [
              { header: "DADOS PESSOAIS", dataKey: "funcionario" },
              { header: "CONTACTOS & DOCUMENTOS", dataKey: "contactosDoc" },
              { header: "TRABALHO & OUTROS", dataKey: "trabalhoOutros" },
            ],
            linesPerItem: 1,
            lineConfigurations: [
              {
                columns: [
                  { header: "DADOS PESSOAIS", dataKey: "funcionario" },
                  { header: "CONTACTOS & DOCUMENTOS", dataKey: "contactosDoc" },
                  { header: "TRABALHO & OUTROS", dataKey: "trabalhoOutros" },
                ],
                style: {
                  fillColor: [248, 249, 250],
                  textColor: [0, 0, 0],
                  fontSize: 9,
                },
              },
            ],
          }
        : {
            filename: "colaboradores",
            title: "LISTA DE COLABORADORES",
            subtitle: "Informacoes basicas dos colaboradores",
            orientation: "portrait",
            showPageNumbers: true,
            columns,
            fontSize: {
              header: 10,
              body: 9,
              title: 14,
            },
          };

      const exportData = isAdmin
        ? allEmployees.map((emp) => ({
            funcionario: `
*** FUNCIONARIO No. ${emp.employeeNumber || "N/A"} ***
===================================

NOME: ${getFullText(emp.name)}
CARGO: ${getFullText(emp.jobTitle?.name)}
NASCIMENTO: ${
              emp.birthDate
                ? new Date(emp.birthDate).toLocaleDateString("pt-PT")
                : "N/A"
            }
ESTADO CIVIL: ${getFullText(emp.maritalStatus)}
FILHOS: ${emp.childrenNumber || "0"}

MORADA:
${getFullText(emp.address)}
${emp.complement ? emp.complement + ", " : ""}${
              emp.postalCode || ""
            } ${getFullText(emp.city)}
${getFullText(emp.country)}

STATUS: ${emp.isActive ? "ATIVO" : "INATIVO"}
        `.trim(),

            contactosDoc: `
CONTACTOS
===================================

Email Corporativo:
${getFullText(emp.emailAddress)}

Email Pessoal:
${getFullText(emp.personalEmail)}

Telefone: ${emp.phoneNumber || "N/A"}
Tel. Profissional: ${emp.professionalPhoneNumber || "N/A"}

DOCUMENTOS
===================================

${getFullText(emp.idDocumentType)}: ${emp.idDocumentNumber || "N/A"}
NIF: ${emp.fiscalIdNumber || "N/A"}
Seg. Social: ${emp.socialSecurityNumber || "N/A"}
Carta Conducao: ${emp.drivingLicenceNumber || "N/A"}
RFI Expira: ${
              emp.rfiExpiresAt
                ? new Date(emp.rfiExpiresAt).toLocaleDateString("pt-PT")
                : "N/A"
            }

EMERGENCIA 
===================================

Nome: ${getFullText(emp.emergencyContactName)}
Relacao: ${getFullText(emp.emergencyContactRelation)}
Telefone: ${emp.emergencyContactPhone || "N/A"}
        `.trim(),

            trabalhoOutros: `
TRABALHO 
===================================

Contrato: ${getFullText(emp.contractType)}
Tipo: ${getFullText(emp.workType)}
Local: ${getFullText(emp.workLocation)}
Pais: ${getFullText(emp.workCountry)}
Admissao: ${
              emp.admissionDate
                ? new Date(emp.admissionDate).toLocaleDateString("pt-PT")
                : "N/A"
            }
Rescisao: ${
              emp.terminationDate
                ? new Date(emp.terminationDate).toLocaleDateString("pt-PT")
                : "N/A"
            }
Gestor: ${getFullText(emp.managerAssociated?.name)}

FINANCEIRO
===================================

Conta: ${getFullText(emp.bankAccount)}
KM/Mes: ${emp.kmValueMonth ? emp.kmValueMonth + " EUR" : "N/A"}

VEICULO
===================================

Proprietario: ${getFullText(emp.carOwner)}
Matricula: ${emp.carRegistration || "N/A"}

FERIAS
===================================

Ano Atual: ${emp.vacationDaysCurrentYear || "0"} dias
Ano Anterior: ${emp.vacationDaysLastYear || "0"} dias

OUTROS
===================================

Suporte: ${emp.isSuportService ? "Sim" : "Nao"}
Obs: ${getFullText(emp.observations) || "N/A"}
Criado: ${
              emp.createdAt
                ? new Date(emp.createdAt).toLocaleDateString("pt-PT")
                : "N/A"
            }
Modificado: ${
              emp.modifiedAt
                ? new Date(emp.modifiedAt).toLocaleDateString("pt-PT")
                : "N/A"
            }
Por: ${getFullText(emp.lastModifiedBy)}
        `.trim(),
          }))
        : allEmployees.map((emp) => ({
            // Para usuário comum: linha única
            employeeNumber: emp.employeeNumber || "-",
            name: getFullText(emp.name),
            jobTitle: getFullText(emp.jobTitle?.name),
            email: getFullText(emp.emailAddress),
            phone: emp.phoneNumber || "-",
            location: getFullText(emp.workLocation),
          }));

      // Debug: verificar quantos registros foram gerados
      if (isAdmin) {
        await exportMultiLineToPdf(exportData, config as MultiLineExportConfig);
      } else {
        await exportToPdf(exportData, config as ExportConfig);
      }

      toast.current?.show({
        severity: "success",
        summary: "PDF Gerado",
        detail: ` ${allEmployees.length} colaboradores exportados com sucesso!`,
        life: 4000,
      });
    } catch (error) {
      console.error(" Erro ao gerar PDF:", error);

      let errorMessage = "Erro ao gerar PDF. Tente novamente.";

      if (error instanceof Error) {
        if (error.message.includes("jsPDF")) {
          errorMessage = "Erro na biblioteca PDF. Recarregue a página.";
        } else if (error.message.includes("autoTable")) {
          errorMessage = "Erro na formatação da tabela.";
        }
      }

      toast.current?.show({
        severity: "error",
        summary: "Erro PDF",
        detail: errorMessage,
        life: 5000,
      });
    }
  };

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (
              typeof value === "object" &&
              value !== null &&
              "constraints" in value
            ) {
              return [
                key,
                Array.isArray(value.constraints)
                  ? value.constraints[0]?.value ?? ""
                  : "",
              ];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail:
        translateRegexMessage(error?.message, "pt") ||
        "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const showActionOptions = (rowData: Employee) => {
    const permFunc = functionsData?.data?.find(
      (f) => f.label === "Colaboradores"
    );
    const permissionsEmployeeGrid = userPermissions?.find(
      (p) => p.functionId === permFunc?.id
    );
    const canEditPerm = permissionsEmployeeGrid?.allowEdit ?? false;
    const canViewPerm = permissionsEmployeeGrid?.allowView ?? false;
    return (
      <div className="spacing-icons">
        {canViewPerm && (
          <i
            className="pi pi-eye cursor-pointer"
            onClick={() => {
              setDocumentsUpload([]);
              navigate(`/employees/edit/${rowData.id}`);
            }}
          />
        )}
        {canEditPerm && (
          <i
            className="pi pi-pencil cursor-pointer"
            onClick={() => {
              setDocumentsUpload([]);
              navigate(`/employees/edit/${rowData.id}`, {
                state: { canEdit: true },
              });
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={employees?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={employees?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) => {
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          });
        }}
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={
          <HeaderEmployeeGrid
            query={lazyParams.filters}
            exportExcel={handleExportExcel}
            exportPdf={handleExportPdf}
          />
        }
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="employeeNumber"
        globalFilterFields={[
          "employeeNumber",
          "name",
          "contractType",
          "jobTitle",
        ]}
      >
        <Column
          field="employeeNumber"
          header={translateText("Number", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          // body={photo}
          // filterField="name"
          // showFilterMatchModes={false}
        />
        <Column
          field="jobTitle.name"
          header={translateText("Function", "pt")}
          sortable
          filter
          filterField="jobTitle"
          body={(rowData) => rowData.jobTitle?.name}
          showFilterMatchModes={false}
          filterElement={(options) => <JobTitleDropdown options={options} />}
        />

        <Column
          field="contractType"
          header={translateText("Contract", "pt")}
          sortable
          filter
          filterField="contractType"
          showFilterMatchModes={false}
          filterElement={(options) => (
            <ContractTypeDropdown options={options} />
          )}
        />
        <Column
          field="isActive"
          header={translateText("Active", "pt")}
          sortable
          filter
          filterField="isActive"
          dataType="string"
          filterElement={(options) => (
            <EmployeeStatusDropdown options={options} />
          )}
          showFilterMatchModes={false}
          body={(rowData: Employee) => (
            <StatusEmployeeSwitch rowData={rowData} />
          )}
        />
        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default EmployeeGrid;
